import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, AlertTriangle, Gift, MessageSquare, Phone, ArrowRight, Sparkles } from "lucide-react";

const WEEKS = [
  { title: "Semaine du 29 juin", items: [
    "Cours comme d'habitude",
    "🇨🇦 Mercredi 1er juillet : DOJO FERMÉ (Fête du Canada)",
    "Reprise jeudi 2 juillet (horaire habituel)",
    "Samedi 4 juillet : 10 h juniors autodéfense · 11 h 30 kickboxing / Muay Thaï",
  ] },
  { title: "Semaine du 6 juillet", items: ["Aucun changement — horaire normal"] },
  { title: "Semaines du 13 et 20 juillet", items: [
    "Quelques ajustements (journées sans cours / spéciales) — détails à venir",
    "Samedi 25 juillet : cours MAINTENU",
    "À partir du jeudi 23 juillet : retour à l'horaire régulier",
  ] },
];

export default function HoraireEte2026() {
  useEffect(() => { document.title = "Horaire d'été 2026 — Institut Impact Élite Martial"; }, []);
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-50" data-testid="horaire-ete-2026">
      {/* Bandeau important */}
      <div className="bg-red-600 text-white" data-testid="ete-banner">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-start gap-3 text-sm">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <p>
            <strong>Horaire été 2026 :</strong> dojo ouvert tout l'été. Fermé <strong>1er juillet</strong> (Fête du Canada).
            Cours spéciaux <strong>4 et 25 juillet</strong>. Retour à l'horaire normal dès le <strong>23 juillet</strong>.
          </p>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 pt-12 pb-6">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-red-400 border border-red-800/60 rounded-full px-4 py-1.5 mb-5">
          <CalendarDays size={14} /> Été 2026 · Val-d'Or
        </div>
        <h1 className="text-4xl sm:text-5xl font-black leading-tight">Horaire d'été 2026</h1>
        <p className="mt-5 text-base md:text-lg text-zinc-300 leading-relaxed">
          Chers membres, cet été le dojo reste <strong className="text-white">actif</strong> afin de préparer une belle rentrée
          et de mettre en place la nouvelle plateforme Kaizen et le suivi des carnets de progression.
        </p>
      </section>

      {/* Cadeau membres */}
      <section className="max-w-4xl mx-auto px-6 py-6">
        <div className="rounded-2xl border border-[#c9a961]/40 bg-gradient-to-b from-[#c9a961]/10 to-[#141414] p-6" data-testid="ete-cadeau">
          <div className="flex items-center gap-2 text-[#c9a961]"><Gift size={20} /><span className="text-xs uppercase tracking-widest">Cadeau de Sensei Ralph</span></div>
          <h2 className="text-xl md:text-2xl font-bold text-white mt-2">Nous payons vos taxes en juillet et septembre</h2>
          <p className="text-sm text-zinc-300 mt-2 leading-relaxed">
            Pour vous remercier de votre fidélité pendant la transition, nous payons les <strong className="text-white">taxes sur tous les abonnements</strong> des
            membres actifs pour <strong className="text-white">juillet</strong> et <strong className="text-white">septembre 2026</strong>.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="text-2xl font-black tracking-widest text-[#c9a961] bg-black/40 rounded-lg px-5 py-2 border border-[#c9a961]/30" data-testid="ete-promo-code">ETE2026</div>
            <p className="text-sm text-zinc-400">Utilisez ce code à votre prochain paiement, ou passez à l'accueil — on l'applique avec vous en 2 minutes.</p>
          </div>
          <p className="text-[11px] text-zinc-500 mt-3">Valide jusqu'au 30 septembre 2026 · réservé aux membres actifs.</p>
        </div>
      </section>

      {/* Horaire détaillé */}
      <section className="max-w-4xl mx-auto px-6 py-6">
        <h2 className="text-lg md:text-2xl font-bold mb-5">Le détail, semaine par semaine</h2>
        <div className="space-y-4">
          {WEEKS.map((w, i) => (
            <div key={i} className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-5" data-testid={`ete-week-${i}`}>
              <h3 className="font-bold text-[#c9a961]">{w.title}</h3>
              <ul className="mt-2 space-y-1.5">
                {w.items.map((it, j) => (
                  <li key={j} className="text-sm text-zinc-300 flex gap-2"><span className="text-red-500">•</span> {it}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="text-xs text-zinc-500 mt-4">Nouveauté : le dojo est désormais <strong className="text-zinc-300">ouvert le samedi matin</strong> cet été.</p>
      </section>

      {/* Feedback / plateforme */}
      <section className="border-t border-zinc-800 bg-gradient-to-b from-red-950/20 to-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-6 py-14 text-center">
          <Sparkles size={28} className="text-[#c9a961] mx-auto" />
          <h2 className="text-2xl font-black mt-3">Aidez-nous à faire évoluer le dojo</h2>
          <p className="mt-3 text-zinc-300 leading-relaxed">
            Depuis plus de <strong className="text-white">16 ans</strong>, nous faisons évoluer notre école. Prenez quelques minutes
            pour explorer votre <strong className="text-white">carnet de progression</strong> sur la nouvelle plateforme, et envoyez-nous vos commentaires.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <Link to="/membres" data-testid="ete-membres-btn" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-bold rounded-full px-7 py-4 text-sm uppercase tracking-wider transition-colors">
              Accéder à mon espace <ArrowRight size={18} />
            </Link>
            <a href="mailto:info@kaizenfranchise.com?subject=Commentaires%20plateforme%20Kaizen" className="inline-flex items-center gap-2 border border-zinc-700 hover:border-zinc-500 text-zinc-200 rounded-full px-7 py-4 text-sm uppercase tracking-wider transition-colors">
              <MessageSquare size={16} /> Envoyer un commentaire
            </a>
          </div>
          <div className="mt-9 flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm text-zinc-400">
            <a href="tel:+18195279446" className="inline-flex items-center gap-2 hover:text-white"><Phone size={15} /> (819) 527-9446</a>
          </div>
          <p className="mt-8 text-xs text-zinc-600 uppercase tracking-wider">Institut Impact Élite Martial · Kaizen System · Bel été et à bientôt sur le tatami 🥋</p>
        </div>
      </section>
    </div>
  );
}
